// =============================================================================
// SIREPPAR - Verificación de autenticidad (Res. 011/2026 I-SEP)
// =============================================================================
// Solo para la SEP: la ruta va con ProtectedRoute roles={ROLES_INTERNOS} y detrás
// de VITE_AUTENTICIDAD_HABILITADA. La reserva real la hace el backend.
// =============================================================================

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  verificacionAutenticidadService,
  type MotivoCotejo,
  type VerificacionDetalle,
  type VerificacionResumida,
} from '../../services/api';
import PasosVerificacion, { pasoDeVerificacion } from '../../components/verificacion/PasosVerificacion';

const ETIQUETA_SUPUESTO: Record<string, string> = {
  ADHESIONES_ART_18_A: 'Adhesiones (Art. 18 inc. a)',
  FICHAS_AFILIACION_ART_20: 'Fichas de afiliación (Art. 20)',
  REGISTRO_AFILIADOS_ART_32: 'Registro de afiliados (Art. 32)',
  DENUNCIA_FUNDADA: 'Denuncia fundada',
};

const ETIQUETA_MOTIVO: Record<MotivoCotejo, string> = {
  DATOS_NO_CORRESPONDEN: 'Datos que no corresponden al padrón',
  NO_INSCRIPTO_EN_DISTRITO: 'No inscripto en el distrito',
  DUPLICADA_O_SIMULTANEA: 'Duplicada o simultánea',
  PROHIBICION_ART_29: 'Prohibición del Art. 29',
  FICHA_FORMALMENTE_INCOMPLETA: 'Ficha formalmente incompleta',
  FALLECIDO_A_LA_SUSCRIPCION: 'Fallecido a la suscripción',
};

function useRefrescarVerificacion(verificacionId: string) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['verificacion-autenticidad', verificacionId] });
    queryClient.invalidateQueries({ queryKey: ['resumen-cotejo', verificacionId] });
    queryClient.invalidateQueries({ queryKey: ['unidades-cotejo', verificacionId] });
    queryClient.invalidateQueries({ queryKey: ['verificaciones-autenticidad'] });
  };
}

function Cifra({ titulo, valor, tono }: { titulo: string; valor: number; tono: string }) {
  return (
    <div className={`rounded-lg border p-4 ${tono}`}>
      <div className="text-xs font-medium uppercase tracking-wide opacity-80">{titulo}</div>
      <div className="text-2xl font-bold mt-1">{valor.toLocaleString('es-AR')}</div>
    </div>
  );
}

function TarjetaCotejo({ verificacion }: { verificacion: VerificacionDetalle }) {
  const refrescar = useRefrescarVerificacion(verificacion.id);
  const puedeCotejar = verificacion.estado === 'ABIERTA' || verificacion.estado === 'EN_COTEJO';

  const { data: resumen } = useQuery({
    queryKey: ['resumen-cotejo', verificacion.id],
    queryFn: () => verificacionAutenticidadService.resumenCotejo(verificacion.id),
  });

  const cotejo = useMutation({
    mutationFn: () => verificacionAutenticidadService.ejecutarCotejo(verificacion.id),
    onSuccess: () => {
      toast.success('Cotejo integral ejecutado');
      refrescar();
    },
  });

  const yaCotejada = !!resumen && resumen.presentadas > 0;
  const motivos = Object.entries(resumen?.descartadasPorMotivo ?? {}) as Array<[MotivoCotejo, number]>;

  return (
    <div className="bg-white rounded-xl shadow-sm p-5 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Cotejo integral</h3>
          <p className="text-sm text-gray-500">
            Totalidad de las fichas contra el padrón, duplicaciones, prohibiciones del Art. 29 e integridad formal (Art. 3.º)
          </p>
        </div>
        {puedeCotejar && (
          <button
            onClick={() => {
              if (!yaCotejada || window.confirm('¿Volver a ejecutar el cotejo? Se conservan la numeración de las fichas y las revisiones de domicilio ya hechas.')) {
                cotejo.mutate();
              }
            }}
            disabled={cotejo.isPending}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
          >
            {cotejo.isPending ? 'Cotejando…' : yaCotejada ? 'Volver a ejecutar' : 'Ejecutar cotejo'}
          </button>
        )}
      </div>

      {!yaCotejada ? (
        <p className="text-sm text-gray-500 border border-dashed border-gray-300 rounded-lg p-4">
          Todavía no se ejecutó el cotejo.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Cifra titulo="Presentadas" valor={resumen.presentadas} tono="border-gray-200 text-gray-900" />
            <Cifra titulo="Aprobadas" valor={resumen.aprobadas} tono="border-green-200 bg-green-50 text-green-800" />
            <Cifra titulo="Descartadas" valor={resumen.descartadas} tono="border-red-200 bg-red-50 text-red-800" />
            <Cifra titulo="Mínimo legal" valor={resumen.minimoLegal} tono="border-blue-200 bg-blue-50 text-blue-800" />
          </div>

          {resumen.aprobadas < resumen.minimoLegal && (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
              Las fichas aprobadas no alcanzan el mínimo legal.
            </p>
          )}

          {resumen.pendientes > 0 && (
            <p className="text-sm text-orange-800 bg-orange-50 border border-orange-200 rounded-lg p-3">
              Hay <strong>{resumen.pendientes}</strong> ficha(s) con domicilio distinto al del padrón, pendientes de revisión.
              No se puede remitir a peritos hasta resolverlas.
            </p>
          )}

          {motivos.length > 0 && (
            <div>
              <div className="text-sm font-medium text-gray-700 mb-2">Descartadas por motivo</div>
              <ul className="text-sm text-gray-600 space-y-1">
                {motivos.map(([motivo, cantidad]) => (
                  <li key={motivo} className="flex justify-between max-w-md">
                    <span>{ETIQUETA_MOTIVO[motivo] ?? motivo}</span>
                    <strong>{cantidad.toLocaleString('es-AR')}</strong>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-gray-400 mt-1">Una ficha puede tener más de un motivo: la suma puede superar el total de descartadas.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
function RevisionPendientes({ verificacion }: { verificacion: VerificacionDetalle }) {
  const refrescar = useRefrescarVerificacion(verificacion.id);
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());
  const [busqueda, setBusqueda] = useState('');
  const enCotejo = verificacion.estado === 'EN_COTEJO';

  const { data: pendientes = [] } = useQuery({
    queryKey: ['unidades-cotejo', verificacion.id, 'PENDIENTE'],
    queryFn: () => verificacionAutenticidadService.unidades(verificacion.id, 'PENDIENTE'),
    enabled: enCotejo,
  });

  const revision = useMutation({
    mutationFn: (decision: 'APROBAR' | 'RECHAZAR') =>
      verificacionAutenticidadService.resolverPendientesDomicilio(verificacion.id, [...seleccion], decision),
    onSuccess: (_datos, decision) => {
      toast.success(`${seleccion.size} ficha(s) ${decision === 'APROBAR' ? 'aprobada(s)' : 'descartada(s)'}`);
      setSeleccion(new Set());
      refrescar();
    },
  });

  if (!enCotejo || pendientes.length === 0) return null;

  const texto = busqueda.trim().toLowerCase();
  const visibles = texto
    ? pendientes.filter((u) => `${u.numeroFicha} ${u.dni} ${u.apellido} ${u.nombre}`.toLowerCase().includes(texto))
    : pendientes;
  const todas = pendientes.every((u) => seleccion.has(u.id));

  const alternar = (id: string) => setSeleccion((previa) => {
    const nueva = new Set(previa);
    if (nueva.has(id)) nueva.delete(id);
    else nueva.add(id);
    return nueva;
  });

  const decidir = (decision: 'APROBAR' | 'RECHAZAR') => {
    const texto = decision === 'APROBAR'
      ? `¿Aprobar ${seleccion.size} ficha(s)? Pasan al universo de la verificación.`
      : `¿Descartar ${seleccion.size} ficha(s)? Quedan descartadas por datos que no corresponden al padrón.`;
    if (window.confirm(texto)) revision.mutate(decision);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-5 space-y-4 border-l-4 border-orange-400">
      <div>
        <h3 className="text-base font-semibold text-gray-900">Revisión de domicilio ({pendientes.length} pendiente{pendientes.length > 1 ? 's' : ''})</h3>
        <p className="text-sm text-gray-500">
          El domicilio de la ficha no coincide con el del padrón (Art. 3.º inc. a). Hay que resolverlas antes de remitir a peritos.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <select
          value={todas ? 'TODAS' : seleccion.size === 0 ? 'NINGUNA' : 'MANUAL'}
          onChange={(e) => setSeleccion(e.target.value === 'TODAS' ? new Set(pendientes.map((u) => u.id)) : new Set())}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="NINGUNA">Ninguna seleccionada</option>
          <option value="TODAS">Todas seleccionadas</option>
          <option value="MANUAL" disabled hidden>{seleccion.size} seleccionada(s)</option>
        </select>
        <input
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar por N.º de ficha, DNI o nombre"
          className="flex-1 min-w-[220px] px-3 py-2 border border-gray-300 rounded-lg text-sm"
        />
        <button
          onClick={() => decidir('APROBAR')}
          disabled={seleccion.size === 0 || revision.isPending}
          className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
        >
          Aprobar
        </button>
        <button
          onClick={() => decidir('RECHAZAR')}
          disabled={seleccion.size === 0 || revision.isPending}
          className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
        >
          Descartar
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b">
              <th className="py-2 w-8"></th>
              <th className="py-2">N.º ficha</th>
              <th className="py-2">DNI</th>
              <th className="py-2">Apellido y nombre</th>
              <th className="py-2">Domicilio en la ficha</th>
              <th className="py-2">Domicilio en el padrón</th>
            </tr>
          </thead>
          <tbody>
            {visibles.map((u) => (
              <tr key={u.id} className="border-b last:border-0 hover:bg-gray-50 cursor-pointer" onClick={() => alternar(u.id)}>
                <td className="py-2">
                  <input type="checkbox" checked={seleccion.has(u.id)} onChange={() => alternar(u.id)} onClick={(e) => e.stopPropagation()} />
                </td>
                <td className="py-2 font-mono">{u.numeroFicha}</td>
                <td className="py-2 font-mono">{u.dni}</td>
                <td className="py-2">{u.apellido}, {u.nombre}</td>
                <td className="py-2">{u.domicilioFicha ?? '—'}</td>
                <td className="py-2">{u.domicilioPadron ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {visibles.length === 0 && <p className="text-sm text-gray-500 py-3">Ninguna ficha coincide con la búsqueda.</p>}
      </div>
    </div>
  );
}

const hoy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fechaCorta = (iso: string | null) => (iso ? iso.slice(0, 10).split('-').reverse().join('/') : '—');

function TarjetaPericia({ verificacion }: { verificacion: VerificacionDetalle }) {
  const refrescar = useRefrescarVerificacion(verificacion.id);
  const { estado } = verificacion;
  const enCotejo = estado === 'EN_COTEJO';
  const enExamen = estado === 'EN_EXAMEN_PERICIAL';
  const conInforme = !!verificacion.fechaInformePericial && !!verificacion.informePericialDocumento;

  const [fechaRemision, setFechaRemision] = useState(hoy());
  const [fechaInforme, setFechaInforme] = useState(hoy());
  const [archivo, setArchivo] = useState<File | null>(null);
  const [senaladas, setSenaladas] = useState<Map<string, string>>(new Map());
  const [busqueda, setBusqueda] = useState('');

  const { data: resumen } = useQuery({
    queryKey: ['resumen-cotejo', verificacion.id],
    queryFn: () => verificacionAutenticidadService.resumenCotejo(verificacion.id),
  });

  const { data: aprobadas = [] } = useQuery({
    queryKey: ['unidades-cotejo', verificacion.id, 'APROBADO'],
    queryFn: () => verificacionAutenticidadService.unidades(verificacion.id, 'APROBADO'),
    enabled: estado !== 'ABIERTA' && !enCotejo,
  });

  const remitir = useMutation({
    mutationFn: () => verificacionAutenticidadService.remitirAPeritos(verificacion.id, fechaRemision),
    onSuccess: () => {
      toast.success('Fichas remitidas a peritos');
      refrescar();
    },
  });

  const informe = useMutation({
    mutationFn: () => verificacionAutenticidadService.registrarInformePericial(
      verificacion.id,
      archivo!,
      fechaInforme,
      [...senaladas].map(([unidadId, indicador]) => ({ unidadId, indicador: indicador.trim() || null })),
    ),
    onSuccess: () => {
      toast.success('Informe pericial registrado');
      setArchivo(null);
      setSenaladas(new Map());
      refrescar();
    },
  });

  const continuar = useMutation({
    mutationFn: () => verificacionAutenticidadService.continuarAlSorteo(verificacion.id),
    onSuccess: () => {
      toast.success('Verificación lista para la determinación de la muestra');
      refrescar();
    },
  });

  if (estado === 'ABIERTA') return null;

  const pendientes = resumen?.pendientes ?? 0;
  const dirigidas = aprobadas.filter((u) => u.origenMuestra === 'DIRIGIDA');
  const texto = busqueda.trim().toLowerCase();
  const visibles = texto
    ? aprobadas.filter((u) => `${u.numeroFicha} ${u.dni} ${u.apellido} ${u.nombre}`.toLowerCase().includes(texto))
    : aprobadas;
  const todas = aprobadas.length > 0 && aprobadas.every((u) => senaladas.has(u.id));

  const alternar = (id: string) => setSenaladas((previa) => {
    const nueva = new Map(previa);
    if (nueva.has(id)) nueva.delete(id);
    else nueva.set(id, '');
    return nueva;
  });

  const escribirIndicador = (id: string, valor: string) => setSenaladas((previa) => new Map(previa).set(id, valor));

  return (
    <div className="bg-white rounded-xl shadow-sm p-5 space-y-4">
      <div>
        <h3 className="text-base font-semibold text-gray-900">Examen pericial preliminar</h3>
        <p className="text-sm text-gray-500">
          Remisión del conjunto a peritos oficiales antes de determinar la muestra; las fichas señaladas integran la muestra dirigida (Arts. 4.º y 5.º)
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div><div className="text-gray-500">Remisión a peritos</div><div className="font-medium">{fechaCorta(verificacion.fechaRemisionPeritos)}</div></div>
        <div><div className="text-gray-500">Informe pericial</div><div className="font-medium">{fechaCorta(verificacion.fechaInformePericial)}</div></div>
        <div><div className="text-gray-500">Fichas examinadas</div><div className="font-medium">{verificacion.fechaRemisionPeritos ? (resumen?.aprobadas ?? 0).toLocaleString('es-AR') : '—'}</div></div>
        <div><div className="text-gray-500">Señaladas</div><div className="font-medium">{conInforme ? (resumen?.senaladas ?? 0).toLocaleString('es-AR') : '—'}</div></div>
      </div>

      {enCotejo && (
        <div className="flex flex-wrap items-end gap-3 border-t pt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de remisión</label>
            <input type="date" value={fechaRemision} max={hoy()} onChange={(e) => setFechaRemision(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <button
            onClick={() => {
              if (window.confirm(`¿Remitir a peritos las ${resumen?.aprobadas ?? 0} fichas aprobadas? Con la remisión concluye el cotejo y ya no se puede volver a ejecutar.`)) remitir.mutate();
            }}
            disabled={pendientes > 0 || !resumen?.aprobadas || !fechaRemision || remitir.isPending}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
          >
            {remitir.isPending ? 'Remitiendo…' : 'Remitir a peritos'}
          </button>
          {pendientes > 0 && <span className="text-sm text-orange-700">Primero hay que resolver las fichas pendientes de domicilio.</span>}
        </div>
      )}

      {enExamen && !conInforme && (
        <div className="space-y-3 border-t pt-4">
          <div className="text-sm font-medium text-gray-700">Registrar el informe pericial</div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Fecha del informe</label>
              <input type="date" value={fechaInforme} max={hoy()} onChange={(e) => setFechaInforme(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Archivo del informe (PDF)</label>
              <input type="file" accept="application/pdf" onChange={(e) => setArchivo(e.target.files?.[0] ?? null)} className="text-sm" />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <select
              value={todas ? 'TODAS' : senaladas.size === 0 ? 'NINGUNA' : 'MANUAL'}
              onChange={(e) => setSenaladas(e.target.value === 'TODAS' ? new Map(aprobadas.map((u) => [u.id, senaladas.get(u.id) ?? ''])) : new Map())}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="NINGUNA">Todas sin señalar</option>
              <option value="TODAS">Todas señaladas</option>
              <option value="MANUAL" disabled hidden>{senaladas.size} señalada(s)</option>
            </select>
            <input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por N.º de ficha, DNI o nombre"
              className="flex-1 min-w-[220px] px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>

          <div className="overflow-x-auto max-h-96 overflow-y-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50">
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 px-2 w-8"></th>
                  <th className="py-2 px-2">N.º ficha</th>
                  <th className="py-2 px-2">DNI</th>
                  <th className="py-2 px-2">Apellido y nombre</th>
                  <th className="py-2 px-2">Indicador pericial (opcional)</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((u) => (
                  <tr key={u.id} className="border-b last:border-0">
                    <td className="py-2 px-2"><input type="checkbox" checked={senaladas.has(u.id)} onChange={() => alternar(u.id)} /></td>
                    <td className="py-2 px-2 font-mono">{u.numeroFicha}</td>
                    <td className="py-2 px-2 font-mono">{u.dni}</td>
                    <td className="py-2 px-2">{u.apellido}, {u.nombre}</td>
                    <td className="py-2 px-2">
                      <input
                        value={senaladas.get(u.id) ?? ''}
                        onChange={(e) => escribirIndicador(u.id, e.target.value)}
                        disabled={!senaladas.has(u.id)}
                        maxLength={500}
                        placeholder={senaladas.has(u.id) ? 'Ej.: firma dubitada' : ''}
                        className="w-full px-2 py-1 border border-gray-200 rounded text-sm disabled:bg-gray-50"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button
            onClick={() => {
              if (window.confirm(`¿Registrar el informe pericial con ${senaladas.size} ficha(s) señalada(s)? Pasan a la muestra dirigida.`)) informe.mutate();
            }}
            disabled={!archivo || !fechaInforme || informe.isPending}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
          >
            {informe.isPending ? 'Registrando…' : 'Registrar informe pericial'}
          </button>
        </div>
      )}

      {conInforme && (
        <div className="space-y-2 border-t pt-4">
          <div className="text-sm text-gray-600">Informe: <strong>{verificacion.informePericialDocumento!.nombreOriginal}</strong></div>
          {dirigidas.length === 0 ? (
            <p className="text-sm text-gray-500">El informe no señaló ninguna ficha.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2">N.º ficha</th>
                  <th className="py-2">DNI</th>
                  <th className="py-2">Apellido y nombre</th>
                  <th className="py-2">Indicador pericial</th>
                </tr>
              </thead>
              <tbody>
                {dirigidas.map((u) => (
                  <tr key={u.id} className="border-b last:border-0">
                    <td className="py-2 font-mono">{u.numeroFicha}</td>
                    <td className="py-2 font-mono">{u.dni}</td>
                    <td className="py-2">{u.apellido}, {u.nombre}</td>
                    <td className="py-2">{u.indicadorPericial ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {enExamen && (
        <div className="flex flex-wrap items-center justify-end gap-3 border-t pt-4">
          {!conInforme && <span className="text-sm text-gray-500">Se habilita con el informe pericial registrado (Art. 4.º).</span>}
          <button
            onClick={() => {
              if (window.confirm('¿Continuar a la determinación de la muestra? Esta etapa queda cerrada.')) continuar.mutate();
            }}
            disabled={!conInforme || continuar.isPending}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
          >
            Continuar a determinación de la muestra
          </button>
        </div>
      )}
    </div>
  );
}

export default function VerificacionAutenticidadPage() {
  const [partidoId, setPartidoId] = useState('');
  const [verificacionId, setVerificacionId] = useState('');

  const [searchParams] = useSearchParams();
  const verificacionDeLaUrl = searchParams.get('verificacion') ?? '';
  
  const { data: verificaciones = [], isLoading } = useQuery({
    queryKey: ['verificaciones-autenticidad'],
    queryFn: () => verificacionAutenticidadService.listar(),
  });

  const partidos = useMemo(() => {
    const unicos = new Map<string, VerificacionResumida['expediente']['partido']>();
    for (const v of verificaciones) unicos.set(v.expediente.partido.id, v.expediente.partido);
    return [...unicos.values()].sort((a, b) => a.denominacion.localeCompare(b.denominacion));
  }, [verificaciones]);

  const delPartido = verificaciones.filter((v) => v.expediente.partido.id === partidoId);

  useEffect(() => {
    const elegida = verificaciones.find((v) => v.id === verificacionDeLaUrl);
    if (elegida) setPartidoId(elegida.expediente.partido.id);
  }, [verificacionDeLaUrl, verificaciones.length]);

  useEffect(() => {
    if (delPartido.some((v) => v.id === verificacionDeLaUrl)) setVerificacionId(verificacionDeLaUrl);
    else setVerificacionId(delPartido.length === 1 ? delPartido[0].id : '');
  }, [partidoId, verificaciones.length]);

  const { data: verificacion } = useQuery({
    queryKey: ['verificacion-autenticidad', verificacionId],
    queryFn: () => verificacionAutenticidadService.detalle(verificacionId),
    enabled: !!verificacionId,
  });

  return (
    <div className="space-y-4">
      <div>
        <span className="inline-block text-[11px] font-bold uppercase tracking-wide bg-orange-100 text-orange-800 px-2.5 py-0.5 rounded-full mb-2">
          Solo uso interno
        </span>
        <h1 className="text-2xl font-bold text-gray-900">Verificación de autenticidad</h1>
        <p className="text-sm text-gray-500">
          Reglamento de Verificación de Autenticidad de Adhesiones y Afiliaciones (Res. N.º 011/2026 I-SEP)
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Partido</label>
          <select
            value={partidoId}
            onChange={(e) => setPartidoId(e.target.value)}
            disabled={isLoading}
            className="w-full max-w-sm px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{isLoading ? 'Cargando…' : '— Seleccionar partido —'}</option>
            {partidos.map((p) => (
              <option key={p.id} value={p.id}>{p.sigla ? `${p.sigla} — ${p.denominacion}` : p.denominacion}</option>
            ))}
          </select>
        </div>

        {delPartido.length > 1 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Verificación</label>
            <select
              value={verificacionId}
              onChange={(e) => setVerificacionId(e.target.value)}
              className="w-full max-w-sm px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">— Seleccionar —</option>
              {delPartido.map((v) => (
                <option key={v.id} value={v.id}>{ETIQUETA_SUPUESTO[v.supuesto] ?? v.supuesto}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {!isLoading && verificaciones.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 text-sm text-gray-500">
          No hay verificaciones de autenticidad abiertas.
        </div>
      )}

      {verificacion && (
        <>
          <div className="bg-white rounded-xl shadow-sm p-5 space-y-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{verificacion.expediente.partido.denominacion}</h2>
                <p className="text-sm text-gray-500">
                  {ETIQUETA_SUPUESTO[verificacion.supuesto] ?? verificacion.supuesto} · Expediente {verificacion.expediente.numero}
                </p>
              </div>
              <span className="text-sm text-gray-500">Mínimo legal: <strong>{verificacion.minimoLegal.toLocaleString('es-AR')}</strong></span>
            </div>
            <PasosVerificacion paso={pasoDeVerificacion(verificacion.estado)} alerta={verificacion.estado === 'EN_SUBSANACION'} />
          </div>

          <TarjetaCotejo verificacion={verificacion} />

          <RevisionPendientes verificacion={verificacion} />

          <TarjetaPericia verificacion={verificacion} />
        </>
      )}
    </div>
  );
}